import { DeleteIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  AttendancePeriod,
  LABOR_CHARTING,
  LaborEntry,
  LaborEntryType,
  LaborIntrapartumTimeLog,
  Procedure,
  WithId,
} from '@hb/shared'
import { doc, updateDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { db } from '../../../backend/db/db'
import { useProfile } from '../../../contexts'
import { useLaborCharting } from './context'

// ── Helpers ──────────────────────────────────────────────────

const formatDuration = (ms: number): string => {
  const totalMins = Math.round(ms / 60_000)
  const hrs = Math.floor(totalMins / 60)
  const mins = totalMins % 60
  if (hrs === 0) return `${mins}m`
  return `${hrs}h ${mins}m`
}

const formatTime = (ts: number): string =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

const toDatetimeLocalValue = (ms: number): string => {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

// ── Admission to Active Management ───────────────────────────

const AdmissionToActiveManagement = () => {
  const { laborEntries, laborOnsetAt } = useLaborCharting()

  const firstActiveEntry = useMemo(() => {
    const activeTypes: LaborEntryType[] = [
      LaborEntryType.VITALS,
      LaborEntryType.CERVICAL_EXAM,
      LaborEntryType.FHR,
      LaborEntryType.CONTRACTION,
      LaborEntryType.MEDICATION,
    ]
    return (
      laborEntries
        .filter(e => activeTypes.includes(e.type))
        .sort((a, b) => a.startedAt - b.startedAt)[0] ?? null
    )
  }, [laborEntries])

  if (!laborOnsetAt) return null

  const durationMs = firstActiveEntry ? firstActiveEntry.startedAt - laborOnsetAt : null

  return (
    <Flex direction="column" gap={1}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.600"
        _dark={{ color: 'gray.400' }}>
        Admission to Active Management
      </Text>
      <Text fontFamily="Hero-New" fontSize="md" color="gray.800" _dark={{ color: 'gray.100' }}>
        {durationMs != null && durationMs >= 0 ? (
          <>
            {formatDuration(durationMs)}{' '}
            <Text as="span" fontSize="xs" color="gray.500">
              (onset {formatTime(laborOnsetAt)} → first active entry{' '}
              {formatTime(firstActiveEntry.startedAt)})
            </Text>
          </>
        ) : (
          <Text as="span" color="gray.400" fontSize="sm">
            {firstActiveEntry
              ? 'Entry precedes onset time — check timestamps'
              : 'No active management entries yet'}
          </Text>
        )}
      </Text>
    </Flex>
  )
}

// ── Add Attendance Period form ────────────────────────────────

const AddAttendancePeriodForm = ({ onAdd }: { onAdd: (p: AttendancePeriod) => Promise<void> }) => {
  const { authUser } = useAuth()
  const [startValue, setStartValue] = useState(() => toDatetimeLocalValue(Date.now()))
  const [endValue, setEndValue] = useState('')
  const [saving, setSaving] = useState(false)

  const handleAdd = async () => {
    const startAt = new Date(startValue).getTime()
    if (!startAt || isNaN(startAt)) return
    const endAt = endValue ? new Date(endValue).getTime() : undefined
    setSaving(true)
    await onAdd({
      id: String(Date.now()),
      startAt,
      endAt: endAt && !isNaN(endAt) ? endAt : undefined,
      addedBy: authUser?.uid ?? 'unknown',
    })
    setSaving(false)
    setStartValue(toDatetimeLocalValue(Date.now()))
    setEndValue('')
  }

  return (
    <Box
      bg="gray.50"
      _dark={{ bg: 'gray.750', borderColor: 'whiteAlpha.200' }}
      borderRadius={6}
      p={3}
      border="1px solid"
      borderColor="gray.200">
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="xs"
        color="gray.600"
        _dark={{ color: 'gray.400' }}
        mb={2}>
        Add Attendance Period
      </Text>
      <Flex gap={2} flexWrap="wrap" align="flex-end">
        <Flex direction="column" gap={1} flex={1} minW="140px">
          <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
            Start
          </Text>
          <Input
            type="datetime-local"
            value={startValue}
            onChange={e => setStartValue(e.target.value)}
            size="sm"
            fontFamily="Hero-New"
          />
        </Flex>
        <Flex direction="column" gap={1} flex={1} minW="140px">
          <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
            End (optional)
          </Text>
          <Input
            type="datetime-local"
            value={endValue}
            onChange={e => setEndValue(e.target.value)}
            size="sm"
            fontFamily="Hero-New"
          />
        </Flex>
        <Button
          colorScheme="pink"
          size="sm"
          fontFamily="Hero-New"
          isLoading={saving}
          isDisabled={!startValue}
          onClick={handleAdd}>
          Add
        </Button>
      </Flex>
      <Button
        mt={2}
        size="xs"
        variant="ghost"
        colorScheme="gray"
        fontFamily="Hero-New"
        onClick={() => setStartValue(toDatetimeLocalValue(Date.now()))}>
        Start Now
      </Button>
    </Box>
  )
}

// ── Attendance period row ─────────────────────────────────────

const AttendancePeriodRow = ({
  period,
  onDelete,
}: {
  period: AttendancePeriod
  onDelete: () => void
}) => {
  const duration = period.endAt ? period.endAt - period.startAt : null

  return (
    <Flex
      align="center"
      gap={2}
      px={3}
      py={2}
      borderBottom="1px solid"
      borderColor="gray.100"
      _dark={{ borderBottomColor: 'whiteAlpha.100' }}>
      <Flex direction="column" flex={1} minW={0} gap={0.5}>
        <Text fontFamily="Hero-New" fontSize="sm" color="gray.700" _dark={{ color: 'gray.200' }}>
          {formatTime(period.startAt)}
          {period.endAt ? ` → ${formatTime(period.endAt)}` : ''}
        </Text>
        <Text fontFamily="Hero-New" fontSize="xs" color="gray.400">
          {duration != null ? formatDuration(duration) : 'In progress'}
        </Text>
      </Flex>
      <IconButton
        aria-label="Remove"
        icon={<DeleteIcon />}
        size="xs"
        variant="ghost"
        colorScheme="red"
        onClick={onDelete}
      />
    </Flex>
  )
}

// ── Procedure log (read-only from entries) ────────────────────

const ProcedureLog = () => {
  const { laborEntries } = useLaborCharting()
  const procedures = useMemo(
    () =>
      laborEntries
        .filter(e => e.type === LaborEntryType.PROCEDURE)
        .sort((a, b) => a.startedAt - b.startedAt),
    [laborEntries],
  )

  if (!procedures.length) {
    return (
      <Text
        fontFamily="Hero-New"
        fontSize="sm"
        color="gray.400"
        _dark={{ color: 'gray.500' }}
        fontStyle="italic">
        No procedures logged yet
      </Text>
    )
  }

  return (
    <Box
      borderRadius={6}
      border="1px solid"
      borderColor="gray.200"
      _dark={{ borderColor: 'whiteAlpha.200' }}
      overflow="hidden">
      {procedures.map(e => {
        const d = e.data as Procedure
        const duration = d.endTime && d.startTime ? formatDuration(d.endTime - d.startTime) : null
        return (
          <Flex
            key={e.id}
            align="center"
            gap={2}
            px={3}
            py={2}
            borderBottom="1px solid"
            borderColor="gray.100"
            _dark={{ borderBottomColor: 'whiteAlpha.100' }}>
            <Flex direction="column" flex={1} minW={0}>
              <Text
                fontFamily="Hero-New"
                fontSize="sm"
                fontWeight={600}
                color="gray.700"
                _dark={{ color: 'gray.200' }}>
                {d.procedureName}
              </Text>
              <Text fontFamily="Hero-New" fontSize="xs" color="gray.400">
                {formatTime(e.startedAt)} {duration ? `· ${duration}` : ''}
              </Text>
            </Flex>
          </Flex>
        )
      })}
    </Box>
  )
}

// ── Main panel ────────────────────────────────────────────────

interface LaborTimeLogPanelProps {
  isOpen: boolean
  onClose: () => void
}

export const LaborTimeLogPanel = ({ isOpen, onClose }: LaborTimeLogPanelProps) => {
  const { assessmentId } = useProfile()
  const { laborEntries } = useLaborCharting()
  // const { intrapartumThresholds } = useTimeLogConfig()

  // Read attendance periods from laborChartingData via context.
  // For now, read from Firestore directly in this component.
  // In a future pass this can be lifted to context.
  const [localPeriods, setLocalPeriods] = useState<AttendancePeriod[]>([])

  const handleAddPeriod = useCallback(
    async (period: AttendancePeriod) => {
      if (!assessmentId) return
      const updated = [...localPeriods, period]
      setLocalPeriods(updated)
      const totalIntrapartumMinutes = computeTotal(updated, laborEntries)
      const timeLog: LaborIntrapartumTimeLog = {
        attendancePeriods: updated,
        totalIntrapartumMinutes,
        updatedOn: Date.now(),
      }
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        intrapartumTimeLog: timeLog,
      }).catch(console.error)
    },
    [assessmentId, localPeriods, laborEntries],
  )

  const handleDeletePeriod = useCallback(
    async (id: string) => {
      if (!assessmentId) return
      const updated = localPeriods.filter(p => p.id !== id)
      setLocalPeriods(updated)
      const totalIntrapartumMinutes = computeTotal(updated, laborEntries)
      const timeLog: LaborIntrapartumTimeLog = {
        attendancePeriods: updated,
        totalIntrapartumMinutes,
        updatedOn: Date.now(),
      }
      await updateDoc(doc(db, LABOR_CHARTING, assessmentId), {
        intrapartumTimeLog: timeLog,
      }).catch(console.error)
    },
    [assessmentId, localPeriods, laborEntries],
  )

  const totalMinutes = useMemo(
    () => computeTotal(localPeriods, laborEntries),
    [localPeriods, laborEntries],
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={8} mx={4}>
        <ModalHeader
          fontFamily="Hero-New"
          fontWeight={700}
          bg="pink.50"
          borderBottom="1px solid"
          borderColor="pink.200"
          borderTopRadius={8}
          _dark={{ bg: 'pink.900', borderBottomColor: 'pink.700' }}>
          Intrapartum Time Log
        </ModalHeader>
        <ModalBody pb={6} px={4} pt={4}>
          <Flex direction="column" gap={6}>
            {/* §4.3: Admission to active management */}
            <AdmissionToActiveManagement />

            {/* §4.3: Direct attendance periods */}
            <Flex direction="column" gap={3}>
              <Text
                fontFamily="Hero-New"
                fontWeight={700}
                fontSize="sm"
                color="gray.700"
                _dark={{ color: 'gray.200' }}>
                Direct Attendance Periods (Present at Bedside)
              </Text>
              {localPeriods.length > 0 && (
                <Box
                  borderRadius={6}
                  border="1px solid"
                  borderColor="gray.200"
                  _dark={{ borderColor: 'whiteAlpha.200' }}
                  overflow="hidden">
                  {localPeriods.map(p => (
                    <AttendancePeriodRow
                      key={p.id}
                      period={p}
                      onDelete={() => handleDeletePeriod(p.id)}
                    />
                  ))}
                </Box>
              )}
              <AddAttendancePeriodForm onAdd={handleAddPeriod} />
            </Flex>

            {/* §4.3: Procedures performed (auto-generated from timeline) */}
            <Flex direction="column" gap={3}>
              <Text
                fontFamily="Hero-New"
                fontWeight={700}
                fontSize="sm"
                color="gray.700"
                _dark={{ color: 'gray.200' }}>
                Procedures Performed
              </Text>
              <ProcedureLog />
            </Flex>

            {/* §4.3: Total intrapartum time — prominently displayed */}
            <Flex
              direction="column"
              gap={1}
              p={4}
              bg="pink.50"
              borderRadius={8}
              _dark={{ bg: 'pink.900' }}>
              <Text
                fontFamily="Hero-New"
                fontSize="xs"
                fontWeight={600}
                color="pink.600"
                _dark={{ color: 'pink.300' }}>
                TOTAL INTRAPARTUM TIME
              </Text>
              <Text
                fontFamily="Hero-New"
                fontSize="2xl"
                fontWeight={700}
                color="pink.700"
                _dark={{ color: 'pink.200' }}>
                {totalMinutes > 0 ? formatDuration(totalMinutes * 60_000) : '—'}
              </Text>
              <Text fontFamily="Hero-New" fontSize="xs" color="gray.500">
                Sum of attendance periods + procedures. Used for billing team AMA code review.
              </Text>
            </Flex>

            {/* §0.3 compliance: AMA code mapping is billing-team-only — not shown here */}
            <Alert status="info" borderRadius={6} py={2} fontSize="xs">
              <AlertIcon boxSize={3} />
              AMA code suggestions based on total intrapartum time are available to the billing team
              in the claim review screen — not displayed to the clinical provider.
            </Alert>

            <Button colorScheme="pink" fontFamily="Hero-New" variant="outline" onClick={onClose}>
              Close
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// ── Pure calculations ─────────────────────────────────────────

function computeTotal(
  periods: AttendancePeriod[],
  entries: WithId<LaborEntry<LaborEntryType>>[],
): number {
  const attendanceMins = periods.reduce((sum, p) => {
    if (!p.endAt) return sum
    return sum + Math.round((p.endAt - p.startAt) / 60_000)
  }, 0)

  const procedureMins = entries
    .filter(e => e.type === LaborEntryType.PROCEDURE)
    .reduce((sum, e) => {
      const d = e.data as Procedure
      if (!d.endTime || !d.startTime) return sum
      return sum + Math.round((d.endTime - d.startTime) / 60_000)
    }, 0)

  return attendanceMins + procedureMins
}
